/**
 * Cliente de la API backend (NestJS).
 * Usa ruta relativa: el navegador llama al mismo origen (puerto 3000) y Next.js
 * reenvía /api/* a la API por la red interna (ver next.config.ts).
 */
function apiBase() {
  return "/api";
}

export class ApiError extends Error {
  status: number;
  body: { message?: string; confirmable?: boolean } & Record<string, unknown>;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = (body ?? {}) as ApiError["body"];
  }
}

function extractError(data: unknown, status: number): string {
  if (data && typeof data === "object" && "message" in data) {
    const m = (data as { message: unknown }).message;
    if (Array.isArray(m)) return m.join(", ");
    if (typeof m === "string") return m;
  }
  return `Error ${status}`;
}

// Interceptor global de sesión caducada: si una petición autenticada devuelve 401,
// la cookie httpOnly ha expirado (o no hay sesión). Como es httpOnly no podemos
// limpiarla desde JS, así que redirigimos al login (el guard del backend ya la
// ignora por expirada). Se excluyen las rutas /auth/* para que el formulario de
// login muestre su propio error y para no rebotar en /auth/me (no logueado = normal).
let redirigiendo = false;
function manejar401(path: string) {
  if (typeof window === "undefined") return; // SSR: nada que redirigir
  if (path.startsWith("/auth/")) return; // login/me/etc. gestionan su propio 401
  if (redirigiendo) return; // evita disparos múltiples de varias llamadas en paralelo
  const { pathname } = window.location;
  if (pathname === "/login") return; // ya estamos en el login → sin bucle
  redirigiendo = true;
  // Conservamos a dónde quería ir para volver tras reautenticarse.
  const destino = pathname + window.location.search;
  const next = destino && destino !== "/" ? `?next=${encodeURIComponent(destino)}` : "";
  window.location.assign(`/login${next}`);
}

// El token web va en una cookie httpOnly; `credentials: 'include'` la envía sola
// (mismo origen vía el proxy de Next). No se añade cabecera Authorization.
async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    credentials: "include",
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) manejar401(path);
    throw new ApiError(extractError(data, res.status), res.status, data);
  }
  return data as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, {});
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}

// Descarga un fichero binario (p. ej. un Excel) respetando la cookie de sesión.
// Toma el nombre del Content-Disposition si viene; si no, usa `fallbackName`.
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`${apiBase()}${path}`, { credentials: "include" });
  if (!res.ok) {
    if (res.status === 401) manejar401(path);
    const data = await res.json().catch(() => ({}));
    throw new ApiError(extractError(data, res.status), res.status, data);
  }
  const cd = res.headers.get("Content-Disposition") ?? "";
  const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? fallbackName;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Cuenta de login (tabla `usuarios`) = usuario que puede reservar.
export type Cuenta = {
  id: string;
  email: string;
  nombre: string | null;
  roles: string[];
  estado: string;
  numero_socio?: string | null;
  puntos?: number;
  nivel?: string | null;
  codigo_referido?: string | null;
  referido_por?: string | null;
  referido_por_nombre?: string | null;
};

// Una entrada del historial de dispositivos móviles vinculados a una cuenta.
export type DispositivoMovil = {
  id: string;
  device_uid: string;
  orden: number;
  es_actual: boolean;
  so: string | null;
  so_version: string | null;
  modelo: string | null;
  creado_en: string;
  ultimo_login_en: string;
};

export type DispositivosMovilResp = { items: DispositivoMovil[]; total: number };

export type Dispositivo = {
  dispositivo_id: string;
  modelo: string | null;
  numero_serie: string | null;
  mac: string | null;
  beacon_uuid: string | null;
  beacon_major: number | null;
  beacon_minor: number | null;
  beacon_tx_power: number | null;
  beacon_adv_int: number | null;
  beacon_pcal: number | null;
  beacon_dev_name: string | null;
  estado: string;
  inmovilizador: boolean;
  barco_id: string | null;
  barco_nombre: string | null;
  barco_borrado: boolean;
  creado_en: string;
};

export type Barco = {
  barco_id: string;
  nombre: string;
  matricula: string | null;
  modelo: string | null;
  puerto_id: string | null;
  puerto_nombre: string | null;
  activo: boolean;
  creado_en: string;
  inmovilizador: boolean; // tiene dispositivo con inmovilizador de arranque
};

// Puerto / localización (Ajustes → Localizaciones): identidad + coords + límite de navegación.
export type Localizacion = {
  id: string;
  clave: string;
  nombre: string;
  lat: number;
  lon: number;
  activo: boolean;
  orden: number;
};

export type EventoLlave = {
  id: string;
  fecha_hora: string;
  tipo: string;
  origen: string;
  resultado: string | null;
  act_id: number | null;
  reserva_id: string | null;
  reserva_referencia: string | null;
  usuario_id: string | null;
  usuario_nombre: string | null;
  actor_id: string | null;
  actor_nombre: string | null;
  motivo: string | null; // inmovilizador: por qué se ordenó
};

export type EventosLlaveResp = { items: EventoLlave[]; total: number };

// Estado del inmovilizador de un barco (GET /barcos/:id/inmovilizador).
export type InmovEstado =
  | { aplica: false }
  | {
      aplica: true;
      estado: "bloqueado" | "desbloqueado" | null; // último confirmado por la placa
      estado_ts: string | null;
      deseado: "bloqueado" | "desbloqueado";
      pendiente: boolean;
      modo: "auto" | "manual";
      manual_en: string | null;
      manual_por: string | null;
      reserva: { reserva_id: string; referencia: string | null } | null;
    };

export type Reserva = {
  reserva_id: string;
  estado: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  usuario_nombre: string | null;
  barco_nombre: string | null;
};

export type EstadoLive = {
  barco_id: string;
  barco_nombre: string;
  ubicacion: string | null;
  reserva_activa: boolean;
  reserva_id: string | null;
  referencia: string | null;
  usuario_nombre: string | null;
  usuario_email: string | null;
  checkin_en: string | null;
  lat: number | null;
  lon: number | null;
  posicion_ts: string | null;
  distancia_puerto: string | null;
  consumo: string | null;
  consumo_ts: string | null;
  puerta: string | null;
  puerta_ts: string | null;
  actualizado_en: string | null;
};

export type RecorridoPunto = { lat: number; lon: number; ts: string };

export type Recorrido = {
  reserva: {
    reserva_id: string;
    referencia: string | null;
    estado: string;
    fecha_inicio: string | null;
    fecha_fin: string | null;
    ubicacion: string | null;
    checkin_en: string | null;
    checkout_en: string | null;
    barco_nombre: string | null;
    puerto_nombre: string | null;
    usuario_nombre: string | null;
    usuario_email: string | null;
    valoracion: number | null;
    valoracion_comentario: string | null;
    valoracion_en: string | null;
    incidencia: boolean;
    incidencia_descripcion: string | null;
    recibo_generado: boolean;
  } | null;
  puntos: RecorridoPunto[];
  resumen: {
    puntos: number;
    litros: number;
    distancia_km: number;
    distancia_nm: number;
    // Distancia en línea recta del puerto de origen a la posición actual del barco. null si falta.
    distancia_puerto_km: number | null;
    distancia_puerto_nm: number | null;
    duracion_min: number | null;
  };
  historial: { estado: string; ocurrido_en: string }[];
};

// Factura de combustible (una por reserva completada). Importes como string (numeric de pg).
export type Factura = {
  id: string;
  numero: string;
  reserva_id: string;
  reserva_referencia: string | null;
  usuario_id: string | null;
  usuario_nombre: string | null;
  usuario_email: string | null;
  barco_id: string | null;
  barco_nombre: string | null;
  litros: string;
  precio_litro: string | null;
  importe_bruto: string | null;
  nivel: string | null;
  descuento_pct: string;
  importe_descuento: string | null;
  iva_pct: string;
  iva_importe: string | null;
  importe_total: string | null;
  concepto: string | null;
  estado: string;
  ultimo_error: string | null;
  cobrado_en: string | null;
  cerrada_en: string | null;
  creado_en: string;
  tiene_tarjeta: boolean;
  tarjeta_brand: string | null;
  tarjeta_last4: string | null;
};

// Un evento del historial de un recibo (GET /facturacion/:id/historial).
export type FacturaEvento = {
  id: string;
  tipo: string;
  estado: string | null;
  intento: number | null;
  mensaje: string | null;
  ocurrido_en: string;
};

// Una fila del informe de consumo (GET /recorridos/informe). En la vista por reserva vienen
// referencia/barco/fecha; los campos de socio solo si se agrupa por socio; `mes` ('YYYY-MM')
// solo si se agrupa por mes.
export type InformeConsumoFila = {
  reserva_id?: string | null;
  referencia?: string | null;
  barco_nombre?: string | null;
  fecha?: string | null; // 'DD/MM/YYYY HH:MM' ya formateada (hora de Madrid)
  usuario_id?: string | null;
  usuario_nombre?: string | null;
  usuario_email?: string | null;
  mes?: string | null;
  salidas: number;
  litros: string;
  km: string;
  minutos: number;
};

// Respuesta paginada del informe de consumo: filas de la página + total de filas + totales globales
// (estos últimos sobre TODO el filtro, no solo la página, para el pie de tabla).
export type InformeConsumoResp = {
  items: InformeConsumoFila[];
  total: number;
  totales: { salidas: number; litros: string; km: string; minutos: number };
};

export type Lectura = {
  id: string;
  fecha_hora: string;
  dispositivo_id: string;
  tipo_mensaje: string;
  barco_id: string | null;
  reserva_id: string | null;
  usuario_id: string | null;
  payload: unknown;
};
